// @Version: 1.0.6
import { Component, Input, OnChanges, SimpleChanges, ViewChild, WritableSignal, forwardRef, signal } from '@angular/core';
import { Observable, Subscription, map, of, startWith, tap } from 'rxjs';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FORMERROR, TRANSLATE_PATH as TRANSLATE_PATH_SHARED } from 'src/app/shared/shared.model';
import { FC_AUTOCOMPLETE, IDropDown, IValidation, TRANSLATE_SUFFIX } from './autocomplete.model';
import { FieldItemAutocompleteService } from './autocomplete.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateDesignPipe } from 'src/app/shared/pipes/translate-design/translate-design.pipe';

@Component({
   selector: 'app-field-item-autocomplete',
   templateUrl: './autocomplete.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemAutocomplete),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemAutocomplete),
         multi: true
      },
      FieldItemAutocompleteService
   ],
   standalone: false
})
export class FieldItemAutocomplete implements ControlValueAccessor, OnChanges, Validators {
   public FORMERROR = FORMERROR;
   public TRANSLATE_PATH_SHARED = TRANSLATE_PATH_SHARED; 
   public TRANSLATE_SUFFIX: typeof TRANSLATE_SUFFIX = TRANSLATE_SUFFIX;
   public FC_AUTOCOMPLETE = FC_AUTOCOMPLETE; 

   public done$$: WritableSignal<boolean> = signal(false);

   public fgAutocomplete: FormGroup = this.fb.group({});
   public filteredList$$: WritableSignal<IDropDown[]> = signal([])
   public optionList: IDropDown[] = [];

   private validation_: IValidation = {} as IValidation;
   private sub_?: Subscription;
   private value_: string | undefined | null = undefined;
   private nr_: string = "-1";
   @Input() 
   set validation (val: IValidation) {
      console.log("validation", val)
      this.validation_ = val;
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setValidators(this.setValidators());
   }
   get validation () {
      return this.validation_;
   }

   @Input() 
   set nr (value: string) {
      this.nr_ = value;
   }
   get nr (): string {
      return this.nr_;
   }
   @Input() translatePath: string = ""; 
   @Input() inputOptionList: IDropDown[] = [];
   @Input() label: string | undefined = undefined;
   @Input() isFinished: boolean = false;
   @Input() clearValueIfDisabled: boolean = true;
   @Input() placeholder: string = "";

   @Input()
   set value (val: string | undefined | null) {  
     this.value_ = val;
   }
   get value (): string {
      return this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.value ?? '';
   }   

   @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

   constructor (private fb: FormBuilder, public pTranslateDesign: TranslateDesignPipe, private sFIA: FieldItemAutocompleteService) {}

   writeValue (value: string | number | undefined | null): void {
      if (value == undefined) return;
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setValue(value);
      
      if (typeof value == 'number' || (typeof value == 'string' && value.trim().length > 0)) {
         this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.markAsDirty();
         this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.markAsTouched();
      }
      this.onValidationChange();
   }
   
   registerOnChange (fn: any): void {
      this.onChange = fn
   }
   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }
   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   private onTouched () {}
   private onChange (_: string) {}
   private onValidationChange: any = () => {};

   setDisabledState (isDisabled: boolean | undefined): void {
      this.filteredList$$.set(this.optionList)
      if (this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)) isDisabled ? this.setDisabledControls() : this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.enable();       
      this.onValidationChange();
   }

   private setDisabledControls (): void {
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.disable();
      if (this.clearValueIfDisabled) this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setValue(""); 
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.markAsTouched(); 
   }

   validate (control: AbstractControl): ValidationErrors | null {
      if (this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.hasValidator(Validators.required) && this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.pristine && ((this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.value == "" || this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.value == -1) && this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.value != 0)) {
         this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setErrors({[FORMERROR.required]: true});
         return {[FORMERROR.required]: true};
      }
      return this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.hasError(FORMERROR.notAllowed) ? {[FORMERROR.notAllowed]: true} : this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.hasError(FORMERROR.required) ? {[FORMERROR.required]: true}  : this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.invalid ? {[FORMERROR.invalid]: true} : null; 
   }

   ngOnChanges (changes: SimpleChanges): void {
      
      if (changes && (changes.inputOptionList && changes.inputOptionList?.currentValue && (JSON.stringify(changes.inputOptionList?.currentValue) != JSON.stringify(changes.inputOptionList?.previousValue)))) {
         let isList: boolean = (changes.inputOptionList?.currentValue && changes.inputOptionList.currentValue.length > 0);
         this.optionList = isList ? changes.inputOptionList?.currentValue : (this.sFIA.transformLists(this.pTranslateDesign.transform(changes.translatePath?.currentValue + "List") ?? []) as IDropDown[]);
         this.initForm(changes.value?.currentValue);
         this.done$$.set(true)
      } else if (changes && !changes.inputOptionList?.currentValue && (changes.translatePath && changes.translatePath?.currentValue && changes.translatePath?.currentValue != changes.translatePath.previousValue)) {
           this.optionList = (this.sFIA.transformLists(this.pTranslateDesign.transform(changes.translatePath?.currentValue + "List") ?? []) as IDropDown[]);
           this.initForm(changes.value?.currentValue);
           this.done$$.set(true)
      }

   }

   private initForm (value: string | undefined | null): boolean {
      this.addControl(value);
      this.onChange(this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.value);

      return true; 
   }

   private onChangeTouched () {
      this.onChange(this.value);
      this.onTouched();
      this.onValidationChange(); 
   }

   private addControl (value: string | undefined | null): boolean {
      this.fgAutocomplete =  this.fb.group({
         [FC_AUTOCOMPLETE.VALUE]: this.fb.control(value, {validators: this.setValidators(), updateOn: 'change'})
      });
      
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.valueChanges.pipe(
         map(value => {
            if (value) {
               this.nr_ = value; 
               const error =  this.optionList.find(elem => elem.desc.toLowerCase() == value.toString().toLowerCase() || elem.key == value);

               if (!error && value != "") {
                  if (!this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.hasError(FORMERROR.required)) this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setErrors({notAllowed: true});   
               }
            }

            this.onChangeTouched();
            return value; 
         }), 
         startWith(''),
         map(value => {
            this.filteredList$$.set(this.optionList.filter(item => value ? typeof value == "number" ? item.key == value : item.desc.toLowerCase().indexOf(value?.toLowerCase()) > -1 : true))
         } )
      ).subscribe();

      return true; 
   }

   public onBlur(): void {
      this.filteredList$$.set(this.optionList)
   }

   private setValidators (): ValidatorFn[] {
      let validator: ValidatorFn[] = []; 
      if (this.validation.required) validator.push(Validators.required); 
      return validator;
   }

   public displayFn = (value: number): string => {
      return this.optionList && this.optionList.find(item => item.key == value) ? this.optionList.find(item => item.key == value)!.desc : "";
   }

   public mousedown (input: Event): void {
      (input.target as HTMLInputElement).blur();
   }

   public keyenter (input:Event): void {
      if (input == null) return;
      const find = this.optionList.find(elem => elem.key.toString().includes((<HTMLInputElement>input.target).value) ? true : elem.desc ? elem.desc.toString().includes((<HTMLInputElement>input.target).value) ? true : false : false);
      if (find) this.setControlValue(find); 
      
      (input.target as HTMLInputElement).blur();
      this.autocomplete.closePanel(); 
   }

   private setControlValue (val: IDropDown) {
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.setValue(val.key, {emitEvent: false});
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.updateValueAndValidity({emitEvent: false});
      this.fgAutocomplete.get(FC_AUTOCOMPLETE.VALUE)?.markAsTouched();
      this.onChangeTouched();
      return val;
   }
}
